import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calendar } from "@/components/ui/calendar";
import { useI18n } from "@/i18n";
import { format } from "date-fns";
import { it as itLocale, enUS, fr as frLocale, es as esLocale, de as deLocale, ja as jaLocale } from "date-fns/locale";
import { ShieldCheck } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOCALES = { it: itLocale, en: enUS, fr: frLocale, es: esLocale, de: deLocale, ja: jaLocale };

export default function BookingForm() {
  const { t, lang } = useI18n();
  const locale = LOCALES[lang] || itLocale;
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    class_type: "coppie",
    time_slot: "19:30",
    guests: 2,
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [availability, setAvailability] = useState({
    config: { active_weekdays: [0, 1, 2, 3, 4, 5, 6], time_start: "11:00", time_end: "22:00", max_per_day: 1 },
    blocked_dates: [],
    full_dates: [],
  });

  useEffect(() => {
    axios.get(`${API}/availability/status`)
      .then((r) => setAvailability(r.data))
      .catch(() => {});
  }, []);

  // Preselect class_type from ?class= URL param and scroll into view
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const c = params.get("class");
      const validClasses = ["coppie", "gruppo", "tartufo", "degustazione", "evento"];
      if (c && validClasses.includes(c)) {
        setForm((f) => ({ ...f, class_type: c }));
        // Give the layout a beat, then scroll to the booking section
        setTimeout(() => {
          document.getElementById("booking")?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 300);
      }
    } catch {}
  }, []);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Build calendar `disabled` matcher: past dates + inactive weekdays + blocked + full
  const disabledMatcher = (d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;
    // JS getDay(): 0=Sunday..6=Saturday. We store Monday=0..Sunday=6
    const jsWd = d.getDay();
    const wd = jsWd === 0 ? 6 : jsWd - 1;
    if (!availability.config.active_weekdays.includes(wd)) return true;
    const iso = format(d, "yyyy-MM-dd");
    if (availability.blocked_dates.includes(iso)) return true;
    if (availability.full_dates.includes(iso)) return true;
    return false;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!date) {
      toast.error(t.booking.pickDate);
      return;
    }
    // Validate time range
    const { time_start, time_end } = availability.config;
    if (form.time_slot < time_start || form.time_slot > time_end) {
      toast.error(`${t.booking.slot}: ${time_start}–${time_end}`);
      return;
    }
    setSubmitting(true);
    try {
      trackEvent("begin_checkout", {
        value: 50,
        currency: "EUR",
        class_type: form.class_type,
        guests: Number(form.guests),
        language: lang,
      });
      const res = await axios.post(`${API}/bookings/checkout`, {
        ...form,
        guests: Number(form.guests),
        date: format(date, "yyyy-MM-dd"),
        language: lang,
        origin_url: window.location.origin,
      });
      // Redirect to Stripe Checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error(err);
      const detail = err?.response?.data?.detail;
      let msg = t.booking.error;
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) {
        const field = detail[0]?.loc?.[detail[0].loc.length - 1];
        msg = field === "email" ? t.booking.invalidEmail : t.booking.error;
      }
      toast.error(msg);
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-[#FAF9F6]/30 focus:border-[#C85A3C] outline-none py-3 text-[#FAF9F6] placeholder-[#FAF9F6]/40 transition-colors caret-[#C85A3C]";
  const labelCls = "block overline mb-2";

  const { time_start, time_end } = availability.config;

  return (
    <section id="booking" className="bg-[#1F1A14] text-[#FAF9F6] relative grain overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-24 sm:py-32 grid lg:grid-cols-12 gap-12 lg:gap-20 relative z-10">
        <div className="lg:col-span-5">
          <p className="overline text-[#C85A3C]">{t.booking.overline}</p>
          <h2 className="font-serif-display text-5xl md:text-6xl mt-4 leading-[1.05]">{t.booking.title}</h2>
          <p className="mt-6 text-[#FAF9F6]/70 text-lg leading-relaxed max-w-md">{t.booking.sub}</p>

          <div className="mt-12 hidden lg:block">
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={disabledMatcher}
              locale={locale}
              className="bg-[#FAF9F6]/5 border border-[#FAF9F6]/15 rounded-sm p-4 text-[#FAF9F6]"
              classNames={{
                day_selected: "bg-[#C85A3C] text-[#FAF9F6] hover:bg-[#A6472D]",
                day_today: "text-[#C85A3C] font-semibold",
                day_disabled: "text-[#FAF9F6]/20 line-through opacity-40",
                head_cell: "flex-1 min-w-[2.5rem] h-8 flex items-center justify-center text-[#FAF9F6]/60 uppercase text-[0.7rem] tracking-[0.15em] font-normal",
                nav_button: "text-[#FAF9F6] hover:text-[#C85A3C]",
                caption_label: "font-serif-display text-xl text-[#FAF9F6]",
                day: "h-full w-full p-0 text-[#FAF9F6] hover:bg-[#FAF9F6]/10 rounded-sm",
              }}
              data-testid="booking-calendar"
            />
            {date && (
              <p className="mt-4 text-[#C85A3C] font-serif-display text-2xl" data-testid="picked-date">
                {format(date, "EEEE d MMMM yyyy", { locale })}
              </p>
            )}
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-6" data-testid="booking-form">
          <div className="sm:col-span-1">
            <label className={labelCls}>{t.booking.name}</label>
            <input
              data-testid="booking-name"
              required
              className={inputCls}
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
            />
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls}>{t.booking.email}</label>
            <input
              data-testid="booking-email"
              type="email"
              inputMode="email"
              autoComplete="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              required
              className={inputCls}
              value={form.email}
              onChange={(e) => update("email", e.target.value.trim())}
            />
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls}>{t.booking.phone}</label>
            <input
              data-testid="booking-phone"
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              required
              className={inputCls}
              value={form.phone}
              onChange={(e) => update("phone", e.target.value)}
            />
          </div>
          <div className="sm:col-span-1">
            <label className={labelCls}>{t.booking.guests}</label>
            <input
              data-testid="booking-guests"
              type="number"
              min={1}
              max={12}
              required
              className={inputCls}
              value={form.guests}
              onChange={(e) => update("guests", e.target.value)}
            />
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>{t.booking.classType}</label>
            <div className="flex flex-wrap gap-3 mt-1">
              {[
                { v: "coppie", label: t.booking.coppie },
                { v: "gruppo", label: t.booking.gruppo },
                { v: "tartufo", label: t.booking.tartufo },
                { v: "degustazione", label: t.booking.degustazione },
              ].map((o) => (
                <button
                  key={o.v}
                  type="button"
                  data-testid={`class-choice-${o.v}`}
                  onClick={() => update("class_type", o.v)}
                  className={`px-5 py-3 border rounded-sm text-sm tracking-wide transition ${
                    form.class_type === o.v
                      ? "bg-[#C85A3C] border-[#C85A3C] text-[#FAF9F6]"
                      : "border-[#FAF9F6]/30 text-[#FAF9F6] hover:border-[#C85A3C]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>{t.booking.slot}</label>
            <input
              type="time"
              data-testid="booking-time"
              required
              value={form.time_slot}
              onChange={(e) => update("time_slot", e.target.value)}
              className="bg-transparent border border-[#FAF9F6]/30 focus:border-[#B89668] outline-none px-4 py-3 text-[#FAF9F6] rounded-sm text-base tracking-wide caret-[#B89668] [color-scheme:dark]"
            />
            <p className="mt-2 text-xs text-[#FAF9F6]/50 leading-relaxed">
              {t.booking.slotHint} · {time_start}–{time_end}
            </p>
          </div>

          <div className="sm:col-span-2 lg:hidden">
            <label className={labelCls}>{t.booking.date}</label>
            <Calendar
              mode="single"
              selected={date}
              onSelect={setDate}
              disabled={disabledMatcher}
              locale={locale}
              className="bg-[#FAF9F6]/5 border border-[#FAF9F6]/15 rounded-sm p-3 text-[#FAF9F6] mx-auto"
              classNames={{
                day_selected: "bg-[#C85A3C] text-[#FAF9F6]",
                day_today: "text-[#C85A3C] font-semibold",
                day_disabled: "text-[#FAF9F6]/20 line-through opacity-40",
                head_cell: "flex-1 min-w-[2.5rem] h-8 flex items-center justify-center text-[#FAF9F6]/60 uppercase text-[0.7rem] tracking-[0.15em] font-normal",
                nav_button: "text-[#FAF9F6]",
                caption_label: "font-serif-display text-xl",
                day: "h-full w-full p-0 text-[#FAF9F6] hover:bg-[#FAF9F6]/10 rounded-sm",
              }}
              data-testid="booking-calendar-mobile"
            />
          </div>

          <div className="sm:col-span-2">
            <label className={labelCls}>{t.booking.message}</label>
            <textarea
              data-testid="booking-message"
              rows={3}
              className={inputCls + " resize-none"}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
            />
          </div>

          <div className="sm:col-span-2 mt-4 flex flex-col sm:flex-row sm:items-center gap-4">
            <button
              type="submit"
              data-testid="booking-submit"
              disabled={submitting}
              className="btn-terracotta px-10 py-4 rounded-sm tracking-[0.2em] uppercase text-xs disabled:opacity-50 w-full sm:w-auto"
            >
              {submitting ? t.booking.sending : `${t.booking.payButton} · ${t.booking.payAmount}`}
            </button>
            <p className="text-xs text-[#FAF9F6]/55 flex items-center gap-2 leading-relaxed">
              <ShieldCheck className="w-4 h-4 text-[#C85A3C] shrink-0" />
              {t.booking.payHint}
            </p>
          </div>
        </form>
      </div>
    </section>
  );
}
